/**
 * Servicio para gestionar la configuración de requisitos de clases por club
 * Cada club puede definir sus propios requisitos para cada clase progresiva
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { ClubClassConfig, ClubClassRequirement } from "@/types";

const COLLECTION = "clubClassConfigs";

export const clubClassConfigService = {
  /**
   * Obtener todas las configuraciones de un club
   */
  async getByClub(clubId: string): Promise<ClubClassConfig[]> {
    const q = query(collection(db, COLLECTION), where("clubId", "==", clubId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(
      (d) => ({ id: d.id, ...d.data() }) as ClubClassConfig,
    );
  },

  /**
   * Obtener la configuración de una clase específica para un club
   */
  async getByClubAndClass(
    clubId: string,
    classId: string,
  ): Promise<ClubClassConfig | null> {
    const q = query(
      collection(db, COLLECTION),
      where("clubId", "==", clubId),
      where("classId", "==", classId),
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() } as ClubClassConfig;
  },

  /**
   * Obtener por ID
   */
  async getById(id: string): Promise<ClubClassConfig | null> {
    const docRef = doc(db, COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() } as ClubClassConfig;
  },

  /**
   * Crear o actualizar la configuración de una clase para un club
   */
  async upsert(
    clubId: string,
    classId: string,
    className: string,
    requirements: Omit<ClubClassRequirement, "id">[],
    userId: string,
  ): Promise<ClubClassConfig> {
    const now = Timestamp.now();

    // Buscar si ya existe
    const existing = await this.getByClubAndClass(clubId, classId);

    // Generar IDs para los requisitos
    const requirementsWithIds: ClubClassRequirement[] = requirements.map(
      (req, index) => ({
        ...req,
        id: `req_${Date.now()}_${index}`,
        order: req.order || index + 1,
      }),
    );

    if (existing) {
      // Actualizar
      const docRef = doc(db, COLLECTION, existing.id);
      await updateDoc(docRef, {
        requirements: requirementsWithIds,
        updatedAt: now,
        updatedBy: userId,
      });
      return {
        ...existing,
        requirements: requirementsWithIds,
        updatedAt: now,
        updatedBy: userId,
      };
    } else {
      // Crear nuevo
      const newDocRef = doc(collection(db, COLLECTION));
      const newConfig: ClubClassConfig = {
        id: newDocRef.id,
        clubId,
        classId,
        className,
        requirements: requirementsWithIds,
        createdAt: now,
        createdBy: userId,
        updatedAt: now,
        updatedBy: userId,
      };
      await setDoc(newDocRef, newConfig);
      return newConfig;
    }
  },

  /**
   * Añadir un requisito a una clase
   */
  async addRequirement(
    configId: string,
    requirement: Omit<ClubClassRequirement, "id">,
    userId: string,
  ): Promise<void> {
    const config = await this.getById(configId);
    if (!config) throw new Error("Configuración no encontrada");

    const newReq: ClubClassRequirement = {
      ...requirement,
      id: `req_${Date.now()}`,
      order: requirement.order || config.requirements.length + 1,
    };

    await updateDoc(doc(db, COLLECTION, configId), {
      requirements: [...config.requirements, newReq],
      updatedAt: Timestamp.now(),
      updatedBy: userId,
    });
  },

  /**
   * Actualizar un requisito
   */
  async updateRequirement(
    configId: string,
    requirementId: string,
    updates: Partial<Omit<ClubClassRequirement, "id">>,
    userId: string,
  ): Promise<void> {
    const config = await this.getById(configId);
    if (!config) throw new Error("Configuración no encontrada");

    const updatedReqs = config.requirements.map((req) =>
      req.id === requirementId ? { ...req, ...updates } : req,
    );

    await updateDoc(doc(db, COLLECTION, configId), {
      requirements: updatedReqs,
      updatedAt: Timestamp.now(),
      updatedBy: userId,
    });
  },

  /**
   * Eliminar un requisito
   */
  async deleteRequirement(
    configId: string,
    requirementId: string,
    userId: string,
  ): Promise<void> {
    const config = await this.getById(configId);
    if (!config) throw new Error("Configuración no encontrada");

    const updatedReqs = config.requirements.filter(
      (req) => req.id !== requirementId,
    );

    await updateDoc(doc(db, COLLECTION, configId), {
      requirements: updatedReqs,
      updatedAt: Timestamp.now(),
      updatedBy: userId,
    });
  },

  /**
   * Eliminar toda la configuración de una clase para un club
   */
  async delete(configId: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, configId));
  },
};
